import React from "react"
import { FormError, FormRule } from "models"
import { isObject } from "utils/validation"
import { produce } from "immer"
import { set } from "lodash"

/*
	lodashSetFormFields
	Wrapper function that uses the lodash set function to allow setting state
	with a givenpath (eg "user.description.phone")
*/
export const lodashSetFormFields = (
	changes: { [key: string]: unknown },
	setFormFields?: React.Dispatch<React.SetStateAction<any>>
) => {
	setFormFields(
		// React change detection is based on Immutability, meaning that React doesnt change
		// when an object property has changed (mutation) but when the whole object changed.
		// Immer "produce" method allow use to be sure that the new returned state is immutable
		// See https://immerjs.github.io/immer/example-setstate
		produce((draft: any) => {
			Object.entries(changes).map(([key, value]) => {
				// Lodash "set" method allows to mutate an object with a new value at the
				// path given (eg "user.description.phone").
				// See https://lodash.com/docs#set
				return set(draft as Record<string, unknown>, key, value)
			})
		})
	)
}

export type HandleEvent = (
	event: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLElement, Element>
) => void

/*
	HandleInputChange
	Generic function that get the change event of any input
	and save it back in a given form fields state so the form
	can be aware of all the values of its input children
*/
export type HandleInputChange = (
	event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	setFormFields?: React.Dispatch<React.SetStateAction<any>>
) => void

export const handleInputChange: HandleInputChange = (event, setFormFields) => {
	const target = event.target
	const value = target.type === "checkbox" ? (target as HTMLInputElement)?.checked : target.value
	const name = target.name

	// Note the handleInputChange is adapted so it can update the leaf properties of
	// a nested formFields (eg. "user.description.phone"). Hence the use here of
	// lodash (set) and immerJs (produce).
	lodashSetFormFields({ [name]: value }, setFormFields)
}

/*
	HandleInputValidation
	Generic function that get an array of errors of a given named input
	and store it in the form state so the form can be aware of all
	the errors of its input children
*/
export type HandleInputValidation = (
	name: string,
	errs: Array<FormError>,
	setErrors?: React.Dispatch<React.SetStateAction<Array<FormError>>>
) => void

export const handleInputValidation: HandleInputValidation = (name, errs, setErrors) => {
	setErrors((s: Array<FormError>) => {
		const cleanErrors = [...s.filter((e: FormError) => e.origin !== name)]
		return [...cleanErrors, ...errs]
	})
}

/*
	HandleInternalInputValidation
	Generic function that get an array of errors of a given named input
	and store it in the form state so the form can be aware of all
	the errors of its input children
*/
export type HandleInternalInputValidation = (
	currentValue: any,
	name: string,
	rules: FormRule[],
	setErrors: React.Dispatch<React.SetStateAction<Array<FormError>>>,
	onValidate: HandleInputValidation
) => void

export const handleInternalInputValidation: HandleInternalInputValidation = (
	currentValue,
	name,
	rules,
	setErrors,
	onValidate
) => {
	const newErrors: React.SetStateAction<FormError[]> = []
	rules.forEach((rule) => {
		if (!rule.check(currentValue)) {
			newErrors.push({
				origin: name,
				rule: rule.key,
				message: rule.message,
			})
		}
	})
	setErrors(newErrors)
	if (onValidate) onValidate(name, newErrors)
}

/*
	formStatusEnum
	Enum of the different status a form can be in
*/
export const formStatusEnum = {
	ready: "ready",
	loading: "loading",
	readonly: "readonly",
	complete: "complete",
	errored: "errored",
	expired: "expired",
}

type FormStatusEnumKeys = keyof typeof formStatusEnum
export type FormStatusEnumValues = (typeof formStatusEnum)[FormStatusEnumKeys]

/*
	FormFieldFloatingLabel
	Render props pattern to provide given child input with floating labels utils
*/
export type StatesAndHelpersFloatingLabel = {
	floatingLabelClassname: string
	onFloatingLabelFocus: () => void
	onFloatingLabelBlur: () => void
	inputAttributeToFixAutofill: Record<string, unknown>
}

type FormFieldFloatingLabelProps = {
	children: (x: StatesAndHelpersFloatingLabel) => React.ReactChild
	inputType: string
	inputValue: any
	forceFloating?: boolean
}

const ALWAYS_FLOAT_TYPES = ["checkbox", "radio", "date", "range", "file"]

export const FormFieldFloatingLabel = ({
	children,
	inputType = "",
	inputValue = undefined,
	forceFloating = false,
}: FormFieldFloatingLabelProps) => {
	const isAlwaysFloatType = ALWAYS_FLOAT_TYPES?.includes(inputType) || forceFloating
	const isChildrenFilled =
		inputValue &&
		Boolean(
			// checking is different if value is an object
			isObject(inputValue) ? Object.keys(inputValue).length : inputValue.length
		)
	const shouldFloat = isAlwaysFloatType || isChildrenFilled

	const [labelIsFloating, setLabelIsFloating] = React.useState<boolean>(shouldFloat)

	const floatingLabelClassname =
		labelIsFloating || shouldFloat ? "c-form-field--label-float-above" : ""

	const setFloatingLabel = (bool: boolean) => {
		if (!isAlwaysFloatType && !isChildrenFilled) setLabelIsFloating(bool)
	}

	const onFloatingLabelFocus = () => setFloatingLabel(true)
	const onFloatingLabelBlur = () => setFloatingLabel(false)

	// Fix: when autofilled or with `-internal-input-suggested`, the input is filled with value
	// but the floating label doesn't trigger.
	// Unfortunately only CSS can detect autofill...
	// Solution: trigger a false css animation when autofilled (cf form.scss) then detect the
	// animation on the input thanks to native onAnimationStart
	const inputAttributeToFixAutofill = {
		onAnimationStart: (event: any) => {
			if (event.animationName === "onAutoFillStart") onFloatingLabelFocus()
			if (event.animationName === "onAutoFillCancel") onFloatingLabelBlur()
		},
	}

	const getStatesAndHelpersFloatingLabel = () => ({
		floatingLabelClassname,
		onFloatingLabelFocus,
		onFloatingLabelBlur,
		inputAttributeToFixAutofill,
	})

	return <>{children(getStatesAndHelpersFloatingLabel())}</>
}
